import { useQuery } from "@tanstack/react-query";
import { Button, Card, DataTable, KpiCard, type Column , fmtBRL} from "@atlas/ui/web";
import { atlas } from "../../lib/sdk";
import { PageHeader, fmtDataISO } from "./_ui";
import { downloadCsv, printScope } from "../../lib/export-relatorio";


export function PrefeituraRelatorios() {
  const vinc = useQuery({ queryKey: ["prefeitura", "rel-vinculo"], queryFn: () => atlas.prefeitura.relServidoresPorVinculo() });
  const margem = useQuery({ queryKey: ["prefeitura", "rel-margem"], queryFn: () => atlas.prefeitura.relMargemMedia() });
  const banco = useQuery({ queryKey: ["prefeitura", "rel-banco"], queryFn: () => atlas.prefeitura.relContratosPorBanco() });
  const inc = useQuery({ queryKey: ["prefeitura", "rel-inc"], queryFn: () => atlas.prefeitura.relInconsistencias() });
  const folhas = useQuery({ queryKey: ["prefeitura", "folhas"], queryFn: () => atlas.prefeitura.folhas() });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <PageHeader title="Relatórios operacionais" subtitle="Servidores por vínculo, margem média, contratos por banco, histórico de folhas e inconsistências. Cada seção pode ser exportada em Excel ou PDF." />

      <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
        <KpiCard label="Servidores" value={margem.data?.servidores ?? 0} />
        <KpiCard label="Margem média total" value={fmtBRL(margem.data?.margemMediaTotal ?? 0)} accent="info" />
        <KpiCard label="Margem média disp." value={fmtBRL(margem.data?.margemMediaDisponivel ?? 0)} accent="success" />
        <KpiCard label="% uso médio" value={`${((margem.data?.percentualUsoMedio ?? 0) * 100).toFixed(1)}%`} />
      </div>

      <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" }}>
        <Card>
          <div data-print-scope="rel-vinculo">
            <SecaoHeader
              titulo="Servidores por vínculo"
              onExcel={() => downloadCsv(
                "relatorio-servidores-por-vinculo",
                vinc.data?.dados ?? [],
                [{ key: "vinculo", label: "Vínculo" }, { key: "total", label: "Total" }],
              )}
              onPdf={() => printScope("rel-vinculo", "Servidores por vínculo")}
            />
            <DataTable
              columns={[{ key: "vinculo", header: "Vínculo" }, { key: "total", header: "Total", align: "right" }] as Column<{ vinculo: string; total: number }>[]}
              rows={vinc.data?.dados ?? []}
              rowKey={(r) => r.vinculo}
              loading={vinc.isLoading}
              emptyState="Nenhum servidor cadastrado ainda."
            />
          </div>
        </Card>
        <Card>
          <div data-print-scope="rel-banco">
            <SecaoHeader
              titulo="Contratos por banco"
              onExcel={() => downloadCsv(
                "relatorio-contratos-por-banco",
                banco.data?.dados ?? [],
                [
                  { key: "banco", label: "Banco" },
                  { key: "contratos", label: "Contratos" },
                  { key: "valorParcela", label: "Parcelas (R$)", format: (v) => Number(v ?? 0).toFixed(2) },
                ],
              )}
              onPdf={() => printScope("rel-banco", "Contratos por banco")}
            />
            <DataTable
              columns={[
                { key: "banco", header: "Banco" },
                { key: "contratos", header: "Contratos", align: "right" },
                { key: "valorParcela", header: "Parcelas", align: "right", render: (r: { valorParcela: number }) => fmtBRL(r.valorParcela) },
              ] as Column<{ banco: string; contratos: number; valorParcela: number }>[]}
              rows={banco.data?.dados ?? []}
              rowKey={(r) => r.banco}
              loading={banco.isLoading}
              emptyState="Nenhum contrato averbado ainda."
            />
          </div>
        </Card>
      </div>

      <Card>
        <div data-print-scope="rel-folhas">
          <SecaoHeader
            titulo="Histórico de folhas processadas"
            onExcel={() => downloadCsv(
              "relatorio-folhas",
              folhas.data?.folhas ?? [],
              [
                { key: "competencia", label: "Competência" },
                { key: "dataCorte", label: "Corte", format: (v) => fmtDataISO(v as string) },
                { key: "dataRepasse", label: "Repasse", format: (v) => fmtDataISO(v as string | null) },
                { key: "status", label: "Status" },
                { key: "movimentacoes", label: "Movimentações" },
              ],
            )}
            onPdf={() => printScope("rel-folhas", "Histórico de folhas")}
          />
          <DataTable
            columns={[
              { key: "competencia", header: "Competência", mono: true },
              { key: "dataCorte", header: "Corte", render: (f: { dataCorte: string }) => fmtDataISO(f.dataCorte) },
              { key: "dataRepasse", header: "Repasse", render: (f: { dataRepasse: string | null }) => fmtDataISO(f.dataRepasse) },
              { key: "status", header: "Status" },
              { key: "movimentacoes", header: "Movimentações", align: "right" },
            ] as Column<{ competencia: string; dataCorte: string; dataRepasse: string | null; status: string; movimentacoes: number }>[]}
            rows={folhas.data?.folhas ?? []} rowKey={(f) => f.competencia} loading={folhas.isLoading} emptyState="Nenhuma folha processada ainda."
          />
        </div>
      </Card>

      <Card>
        <div data-print-scope="rel-inc">
          <SecaoHeader
            titulo={`Inconsistências (${inc.data?.total ?? 0})`}
            onExcel={() => downloadCsv(
              "relatorio-inconsistencias",
              inc.data?.inconsistencias ?? [],
              [
                { key: "matricula", label: "Matrícula" },
                { key: "nome", label: "Servidor" },
                { key: "problema", label: "Problema" },
              ],
            )}
            onPdf={() => printScope("rel-inc", "Inconsistências")}
          />
          <DataTable
            columns={[{ key: "matricula", header: "Matrícula", mono: true }, { key: "nome", header: "Servidor" }, { key: "problema", header: "Problema", render: (r: { problema: string }) => <span style={{ color: "var(--danger-500)" }}>{r.problema}</span> }] as Column<{ matricula: string; nome: string; problema: string }>[]}
            rows={inc.data?.inconsistencias ?? []}
            rowKey={(r) => `${r.matricula}-${r.problema}`}
            loading={inc.isLoading}
            emptyState="Nenhuma inconsistência."
          />
        </div>
      </Card>
    </div>
  );
}

/** Cabecalho de secao com titulo + botoes Excel/PDF. Cliente pediu 31/07/2026:
 *  todo relatorio da prefeitura tem que poder ser exportado nesses dois formatos. */
function SecaoHeader({ titulo, onExcel, onPdf }: { titulo: string; onExcel: () => void; onPdf: () => void }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
      <span style={eyebrow}>{titulo}</span>
      <div className="no-print" style={{ display: "flex", gap: 6 }}>
        <Button size="sm" variant="ghost" onClick={onExcel} title="Baixar em Excel (CSV/UTF-8)">↓ Excel</Button>
        <Button size="sm" variant="ghost" onClick={onPdf} title="Imprimir / salvar como PDF">↓ PDF</Button>
      </div>
    </div>
  );
}

const eyebrow: React.CSSProperties = { fontSize: 11, fontWeight: 700, letterSpacing: ".08em", color: "var(--text-dim)", textTransform: "uppercase" };
